import React, { useEffect, useState } from 'react';
import { CFormLabel } from '@coreui/react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { colors, formatDateToBackend, openFileWithLoading } from '../components/Helper'
import { reportNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import PieChart from '../components/PieChart';
import LineChart from '../components/LineChart';
import PageHeader from '../components/PageHeader';
import DateRangeSelection from '../components/DateRangeSelection';
import PrintReceiptLayout from '../components/PrintReceiptLayout';
import ViewSingleReceiptReport from '../components/ViewSingleReceiptReport';
import '../styles/PrintReceipt.css';

function ReceiptReport() {
    const { receiptReport, receiptReportCharts, receiptReportPDF, singleReceiptReport } = useReport();
    const { currency } = useCompanySetting()
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [printReceiptData, setPrintReceiptData] = useState(null);
    const [receiptId, setReceiptId] = useState(null);
    const [reportList, setReportList] = useState([])
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [paymentChartData, setPaymentChartData] = useState([])
    const [hourlyRevenue, setHourlyRevenue] = useState({
        x: [],
        y: []
    })
    const [hourlyReceipt, setHourlyReceipt] = useState({
        x: [],
        y: []
    })

    const startDate = (dates.length > 0 && dates[0]) ? formatDateToBackend(dates[0]) : null
    const endDate = (dates.length > 0 && dates[1]) ? formatDateToBackend(dates[1]) : null

    const fetchReceiptReport = async () => {
        try {
            setIsLoading(true);
            setReportList([]);
            setTotalRecords(0);

            const reports = await receiptReport(currentPage, rowsPerPage, sortField, sortOrder, startDate, endDate);
            if (reports.data.length > 0) {
                setReportList(reports.data);
                setTotalRecords(reports.totalCount);
            }

        } catch (error) {
            console.error('Error fetching receipt reports', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchReceiptReport();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId, startDate, endDate]);

    const fetchReceiptReportCharts = async () => {
        try {
            setIsLoading(true);

            const reports = await receiptReportCharts(startDate, endDate);
            if (reports.paymentChart.length > 0) {
                const paymentData = reports.paymentChart.map((item, index) => ({
                    id: item.id,
                    value: parseFloat(item.value),
                    label: item.label,
                    color: colors[index % colors.length]
                }));

                setPaymentChartData(paymentData);
            } else {
                setPaymentChartData([]);
            }

            if (reports.hourlyRevenue.length > 0) {
                const hours = reports.hourlyRevenue.map(item => item.hour);
                const revenues = reports.hourlyRevenue.map(item => parseFloat(item.revenue));

                setHourlyRevenue({
                    x: hours,
                    y: revenues
                })
            } else {
                setHourlyRevenue({
                    x: [],
                    y: []
                })
            }

            if (reports.hourlyOrders.length > 0) {
                const hours = reports.hourlyOrders.map(item => item.hour);
                const orders = reports.hourlyOrders.map(item => parseFloat(item.orders));

                setHourlyReceipt({
                    x: hours,
                    y: orders
                })
            } else {
                setHourlyReceipt({
                    x: [],
                    y: []
                })
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching receipt report charts', error);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchReceiptReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchReceiptReport()
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const generatePDF = async (pdfType) => {
        openFileWithLoading({
            apiCall: receiptReportPDF,
            apiParams: [startDate, endDate, pdfType],
            fileType: 'pdf',
            loadingMessage: `Generating ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF...`,
            errorMessage: `Failed to generate ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF`,
        });
    }

    const handlePrintReceipt = async (orderId) => {
        try {
            setIsLoading(true);
            const data = await singleReceiptReport(orderId);
            setPrintReceiptData(data.order);

            setTimeout(() => {
                const printSection = document.getElementById('print-section');
                if (printSection) {
                    printSection.style.display = 'block';
                    window.print();
                    printSection.style.display = 'none';
                }
            }, 500);
        } catch (error) {
            console.error("Error printing receipt:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Receipt Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-between'>
                    <p className='pt-2'>
                        Print or download the report for today: <span className='view_button' onClick={() => generatePDF('summary')}>Summary PDF</span> / <span className='view_button' onClick={() => generatePDF('detailed')}>Detailed PDF</span>
                    </p>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates}
                    />
                </div>
                <div className='px-4 py-2'>
                    {
                        receiptId && (
                            <ViewSingleReceiptReport
                                receiptId={receiptId}
                                setReceiptId={setReceiptId}
                            />
                        )
                    }
                    <div className='report-chart-section d-flex justify-content-between mt-3'>
                        {
                            hourlyRevenue.x.length > 0 && hourlyRevenue.y.length > 0 && (
                                <div className='w-100 text-center me-5'>
                                    <CFormLabel>Revenue</CFormLabel>
                                    <LineChart
                                        x={hourlyRevenue.x}
                                        y={hourlyRevenue.y}
                                        yLabel={'Revenue'}
                                    />
                                </div>
                            )
                        }
                        {
                            hourlyReceipt.x.length > 0 && hourlyReceipt.y.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Receipt</CFormLabel>
                                    <LineChart
                                        x={hourlyReceipt.x}
                                        y={hourlyReceipt.y}
                                        yLabel={'Receipts'}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Payment</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Payment Amount</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark report-table"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={reportList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No receipt reports found."
                        >
                            <Column
                                field="createdAt"
                                header="Created On"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="id"
                                header="Receipt ID"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="total"
                                header="Total Price"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {currency}{rowData.total}
                                    </div>
                                )}
                            />
                            <Column
                                field="change"
                                header="Change"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {currency}{rowData.change}
                                    </div>
                                )}
                            />
                            <Column
                                field="tip"
                                header="Tip"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {currency}{rowData.tip}
                                    </div>
                                )}
                            />
                            <Column
                                field="status"
                                header="Status"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.status === 'in_progress' ? 'in progress' : rowData.status}
                                    </div>
                                )}
                            />
                            <Column
                                field="payment_methods"
                                header="Payments"
                                className="receipt_report_table"
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.payment_methods && rowData.payment_methods.length > 0 ? (
                                            rowData.payment_methods.map((p) => (
                                                <div key={p.id}>
                                                    {currency}{p.amount}/{p.name}
                                                </div>
                                            ))
                                        ) : (
                                            '--'
                                        )}
                                    </div>
                                )}
                            />
                            <Column
                                field="createdBy"
                                header="Created by"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="paymentBy"
                                header="Payment by"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.paymentBy ? rowData.paymentBy : '-'}
                                    </div>
                                )}
                            />
                            <Column
                                field="tableName"
                                header="Table"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.tableName ? rowData.tableName : '-'}
                                    </div>
                                )}
                            />
                            <Column
                                field="customerName"
                                header="Customer"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.customerName ? rowData.customerName : 'n/a'}
                                    </div>
                                )}
                            />
                            <Column
                                field="no_of_persons"
                                header="#Cust."
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="updatedAt"
                                header="Modified On"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                header="Action"
                                align="left"
                                className="receipt_report_table"
                                body={(rowData) => (
                                    <div className="view_button d-flex justify-content-between">
                                        <p onClick={() => setReceiptId(rowData.id)}>View</p>
                                        <p
                                            onClick={() => rowData.status === 'paid' && handlePrintReceipt(rowData.id)}
                                            className={rowData.status !== 'paid' ? 'disabled' : ''}
                                        >
                                            Print
                                        </p>
                                    </div>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>
            <PrintReceiptLayout
                printReceiptData={printReceiptData}
            />
        </Layout2>
    );
}

export default ReceiptReport;

